import store from "./store";
import router from "./router";
import config from "./config";
import Api from "./api";

require("./features/points/points.store");

const SCREEN_ROUTES = {
    login: "/",
    app: "/dashboard",
};

const BALANCE_RATIO = 0.6;

let lastProgram = null;
let place = null;
let balanceSignature = null;
let previewTierIds = [];

function isoDateTime(date) {
    const pad = (value) => String(value).padStart(2, "0");

    return (
        date.getFullYear() +
        "-" + pad(date.getMonth() + 1) +
        "-" + pad(date.getDate()) +
        " " + pad(date.getHours()) +
        ":" + pad(date.getMinutes())
    );
}

function seedTransactions(balance) {
    if (balance <= 0) {
        return [];
    }

    const now = new Date();
    const second = Math.round(balance * 0.3);
    const third = Math.round(balance * 0.2);
    const amounts = [balance - second - third, second, third];

    return amounts
        .filter((amount) => amount > 0)
        .map((amount, index) => {
            const date = new Date(now.getFullYear(), now.getMonth() - index, 12, 14, 30);

            return {
                id: "preview-credit-" + index,
                type: "credit",
                amount: amount,
                date: isoDateTime(date),
                orderDate: isoDateTime(date),
            };
        });
}

function simulatedBalance(rewards) {
    const signature = rewards.map((reward) => reward.id + ":" + reward.points).join("|");

    if (signature === balanceSignature) {
        return store.state.dashboard.balance;
    }

    balanceSignature = signature;

    const highest = rewards.reduce(
        (max, reward) => Math.max(max, Number(reward.points) || 0),
        0
    );

    const balance = Math.round(highest * BALANCE_RATIO);

    store.commit("dashboard/setTransactions", seedTransactions(balance));

    return balance;
}

function todayIso() {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, "0");
    return now.getFullYear() + "-" + pad(now.getMonth() + 1) + "-" + pad(now.getDate());
}

function isWithinDateWindow(reward) {
    const today = todayIso();
    const start = reward.startDate || "";
    const end = reward.endDate || "";

    return (!start || start <= today) && (!end || end >= today);
}

function previewTiers(program) {
    const pointsEnabled = !!(program.points && program.points.enabled);
    const tiers = program.tiers || {};

    if (!pointsEnabled) {
        return null;
    }

    return (tiers.items || [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((tier) => ({
            id: String(tier.id),
            name: tier.name,
            description: tier.description,
            becomingDescription: tier.becomingDescription,
            position: tier.position,
            color: tier.color,
            textColor: tier.textColor,
            iconType: tier.iconType,
            iconEmoji: tier.iconEmoji,
            iconSvg: tier.iconSvg,
            isMember: previewTierIds.includes(String(tier.id)),
        }));
}

function withAccess(rewards, tiers) {
    const memberIds = tiers.filter((tier) => tier.isMember).map((tier) => tier.id);

    return rewards.reduce((result, reward) => {
        const tierIds = (reward.tierIds || []).map(String);
        const canRedeem = !tierIds.length || tierIds.some((tierId) => memberIds.includes(tierId));

        if (canRedeem) {
            result.push({ ...reward, access: "available", tierIds });
        } else if (reward.privacy === "blurred") {
            result.push({ id: reward.id, access: "blurred", tierIds });
        } else if (reward.privacy !== "tier_only") {
            result.push({ ...reward, access: "locked", tierIds });
        }

        return result;
    }, []);
}

function render() {
    if (!lastProgram) {
        return;
    }

    const color = lastProgram.primaryColor || "#2aaa4c";

    store.commit("setPlace", {
        ...(place || {}),
        id: place ? place.id : "preview",
        name: lastProgram.name || (place && place.name) || "",
        color: color,
        mainColor: color,
        logo: place ? place.logo : "",
        benefit_program: {
            ...lastProgram,
            logo: lastProgram.logo || (place && place.benefit_program && place.benefit_program.logo) || "",
            pointsProgramActive: !!(lastProgram.points && lastProgram.points.enabled),
        },
    });

    const items = (lastProgram.points && lastProgram.points.items) || [];
    const visible = items.filter((reward) => reward.active !== false && isWithinDateWindow(reward));
    const tiers = previewTiers(lastProgram);

    store.commit("dashboard/setData", {
        banners: lastProgram.banners || [],
        rewards: tiers ? withAccess(visible, tiers) : visible,
        tiers: tiers || [],
        coupons: [],
        balance: simulatedBalance(visible),
    });

    store.commit("setGlobalStatus", "loaded");
}

function loadPlace() {
    const slug = config.placeSlug;

    if (!slug) {
        return;
    }

    Api.get("place/" + slug + "/public-info")
        .then((response) => {
            place = response.body || null;
            render();
        })
        .catch(() => {});
}

function goToScreen(screen) {
    const path = SCREEN_ROUTES[screen] || SCREEN_ROUTES.app;

    if (router.currentRoute.path !== path) {
        router.push(path).catch(() => {});
    }
}

function onMessage(event) {
    if (event.origin !== config.adminOrigin) {
        return;
    }

    const data = event.data || {};

    if (data.type !== "kevi-preview") {
        return;
    }

    if (Array.isArray(data.previewTierIds)) {
        previewTierIds = data.previewTierIds.map(String);
    }

    if (data.program) {
        lastProgram = data.program;
        render();
    }

    if (data.screen) {
        goToScreen(data.screen);
    }
}

export function setupPreview() {
    window.addEventListener("message", onMessage);
    loadPlace();

    if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: "kevi-preview-ready" }, config.adminOrigin);
    }
}
